import { useEffect } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { type Dispatch, type UnknownAction } from "redux";

import {
  addManager as addManagerAction,
  addManagerReset as addManagerResetAction,
  updateManager as updateManagerAction,
  updateManagerReset as updateManagerResetAction,
} from "../../../../actions";
import { WFPButton } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Spinner } from "../../../../components/ui/spinner";
import { isEmpty } from "../../../../utils";
import { ManagerForm, MANAGER_FORM_ID, type ManagerFormValues } from "../Form";
import { type Country, type Manager } from "../../types";

interface OwnProps {
  manager: Manager | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

interface StateProps {
  countries: Country[];
  fieldErrors: { data?: Record<string, unknown> };
  isLoading: boolean;
}

interface DispatchProps {
  addManager: (manager: ManagerPayload) => Promise<unknown>;
  updateManager: (id: number | string, manager: ManagerPayload) => Promise<unknown>;
  resetManagerErrors: () => void;
}

interface ManagerPayload {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  countries: (number | string)[];
}

interface RootState {
  addManager: { error?: Record<string, unknown>; isLoading: boolean };
  updateManager: { error?: { data?: Record<string, unknown> }; isLoading: boolean };
  userDetail: { data: { countries?: Country[] } };
}

type ManagerModalProps = OwnProps & StateProps & DispatchProps;

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction) as unknown as Promise<unknown>;

const adaptFormValues = (values: ManagerFormValues): ManagerPayload => ({
  first_name: values.first_name,
  last_name: values.last_name,
  email: values.email,
  phone_number: "",
  countries: values.countries.map(country => country.value),
});

const adaptInitialValues = (manager: Manager | null): Partial<ManagerFormValues> | undefined =>
  manager
    ? {
        first_name: manager.first_name ?? "",
        last_name: manager.last_name ?? "",
        email: manager.email ?? "",
        countries: (manager.countries ?? []).map(({ id, name }) => ({ value: id, label: name })),
      }
    : undefined;

export const ManagerModal = ({
  manager,
  isOpen,
  onClose,
  onSaved,
  countries,
  fieldErrors,
  isLoading,
  addManager,
  updateManager,
  resetManagerErrors,
}: ManagerModalProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      resetManagerErrors();
    }
  }, [isOpen, resetManagerErrors]);

  const onValidate = (formValues: ManagerFormValues) => {
    const payload = adaptFormValues(formValues);
    const promise = manager?.id ? updateManager(manager.id, payload) : addManager(payload);

    promise.then(result => {
      if (result) {
        onSaved();
      }
    });
  };

  const preventCloseWhileSaving = (event: Event) => {
    if (isLoading) {
      event.preventDefault();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && !isLoading && onClose()}>
      <DialogContent
        className="sm:max-w-xl"
        aria-describedby={undefined}
        showCloseButton={!isLoading}
        onEscapeKeyDown={preventCloseWhileSaving}
        onPointerDownOutside={preventCloseWhileSaving}
      >
        <DialogHeader>
          <DialogTitle>
            {manager ? t("managerDetails.title") : t("managers.newManagerFormTitle")}
          </DialogTitle>
        </DialogHeader>
        <ManagerForm
          countries={countries}
          fieldErrors={fieldErrors}
          initialValues={adaptInitialValues(manager)}
          onValidate={onValidate}
        />
        <DialogFooter>
          <WFPButton type="button" variant="outline" disabled={isLoading} onClick={onClose}>
            {t("common.cancel")}
          </WFPButton>
          <WFPButton type="submit" form={MANAGER_FORM_ID} disabled={isLoading}>
            {isLoading && <Spinner className="size-4" />}
            {t("common.save")}
          </WFPButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  let fieldErrors: { data?: Record<string, unknown> } = {};
  if (!isEmpty(state.addManager.error)) {
    fieldErrors = { data: state.addManager.error };
  } else if (!isEmpty(state.updateManager.error)) {
    fieldErrors = state.updateManager.error ?? {};
  }

  return {
    countries: state.userDetail.data.countries ?? [],
    fieldErrors,
    isLoading: state.addManager.isLoading || state.updateManager.isLoading,
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  addManager: manager => dispatchAction(dispatch, addManagerAction(manager)),
  updateManager: (id, manager) => dispatchAction(dispatch, updateManagerAction(id, manager)),
  resetManagerErrors: () => {
    dispatchAction(dispatch, addManagerResetAction());
    dispatchAction(dispatch, updateManagerResetAction());
  },
});

export const ConnectedManagerModal = connect(mapStateToProps, mapDispatchToProps)(ManagerModal);
